import { apiClient } from "@/lib/api";
import type { StandardResponse } from "@/types/api-response";
import { extractData } from "@/types/api-response";
import type {
  TldCatalogDto,
  CreateTldCatalogRequest,
  UpdateTldCatalogRequest,
} from "@/types/domain";

/**
 * Admin TLD Catalog Service
 *
 * CRUD for the TLD catalog — the curated, admin-controlled list of
 * sellable top-level domains with retail pricing.
 */
export const adminTldCatalogService = {
  /**
   * Get all TLD catalog entries.
   *
   * The list endpoint is not paginated — it returns the full catalog.
   */
  async getAll(): Promise<TldCatalogDto[]> {
    const response = await apiClient.get<StandardResponse<TldCatalogDto[]>>(
      "/api/v1/tld-catalog-admin",
    );
    return extractData(response.data);
  },

  /**
   * Create a new TLD catalog entry.
   *
   * @param data - TLD catalog creation data
   */
  async create(data: CreateTldCatalogRequest): Promise<TldCatalogDto> {
    const response = await apiClient.post<StandardResponse<TldCatalogDto>>(
      "/api/v1/tld-catalog-admin",
      data,
    );
    return extractData(response.data);
  },

  /**
   * Update an existing TLD catalog entry.
   *
   * Returns the updated TLD catalog entry (backend responds with
   * `StandardResponse<TldCatalogAdminDto>`, HTTP 200).
   *
   * @param id - TLD catalog entry ID (UUID)
   * @param data - TLD catalog update data
   */
  async update(
    id: string,
    data: UpdateTldCatalogRequest,
  ): Promise<TldCatalogDto> {
    const response = await apiClient.put<StandardResponse<TldCatalogDto>>(
      `/api/v1/tld-catalog-admin/${id}`,
      data,
    );
    return extractData(response.data);
  },

  /**
   * Enable or disable a TLD catalog entry.
   *
   * Returns the updated TLD catalog entry (backend responds with
   * `StandardResponse<TldCatalogAdminDto>`, HTTP 200).
   *
   * @param id - TLD catalog entry ID (UUID)
   * @param isEnabled - Whether the TLD should be sellable
   */
  async setEnabled(id: string, isEnabled: boolean): Promise<TldCatalogDto> {
    const response = await apiClient.put<StandardResponse<TldCatalogDto>>(
      `/api/v1/tld-catalog-admin/${id}/enabled`,
      { isEnabled },
    );
    return extractData(response.data);
  },
};
